/**
 * The four expressions captured for a Botox face analysis, in order.
 *
 * Each pose's `verify(score)` decides whether the live face is currently
 * performing that expression, using MediaPipe FaceLandmarker blendshape
 * scores (ARKit-style, 0..1). `score(name)` returns the score for a named
 * blendshape (0 if absent).
 *
 * The expressions map directly onto the muscle groups a Botox plan targets:
 *   - frown        -> corrugator / glabella ("11" lines)
 *   - brow_raise   -> frontalis (forehead lines)
 *   - smile        -> orbicularis oculi (crow's feet) + zygomaticus
 *   - squint       -> orbicularis oculi (lateral crow's feet, isolated)
 *   - lip_purse    -> orbicularis oris (perioral / "smoker's" lines)
 *   - neutral      -> resting baseline for comparison
 */

/** Average of several blendshape scores. */
function avg(score, names) {
  return names.reduce((s, n) => s + score(n), 0) / names.length;
}
/** Max of several blendshape scores. */
function max(score, names) {
  return Math.max(...names.map((n) => score(n)));
}

const EXPRESSION_KEYS = [
  'browDownLeft', 'browDownRight', 'browInnerUp', 'browOuterUpLeft',
  'browOuterUpRight', 'mouthSmileLeft', 'mouthSmileRight', 'jawOpen',
  'eyeSquintLeft', 'eyeSquintRight', 'mouthPucker',
];

/** Highest "active expression" score — used to detect a truly neutral face. */
function maxExpression(score) {
  return max(score, EXPRESSION_KEYS);
}

// Head-angle poses (laterals + up/down) verify HEAD ORIENTATION (degrees from
// headPoseFromMatrix) instead of expression, and are intentionally NOT frontal,
// so they set `requireFrontal: false` — the frontal quality gate is skipped for
// them while presence, framing and lighting still apply. If left/right or
// up/down feel swapped on your device, flip the comparison sign below (or swap
// the two pose objects); the sign comes from the MediaPipe matrix convention.
const LATERAL_YAW = 25; // min |yaw| degrees to count as a turned (3/4) profile
const LOOK_PITCH = 18;  // min |pitch| degrees to count as looking up / down

export const POSES = [
  {
    key: 'neutral',
    // Neutral = no strong expression anywhere. Threshold raised to 0.45 so the
    // "normal" (relaxed) face registers easily, tolerating minor residual
    // expression. Only this expression cutoff changes — framing/positioning/
    // frontal checks (qualityChecks.js) are untouched.
    verify: (score) => maxExpression(score) < 0.45,
  },
  {
    key: 'frown',
    // Both inner brows pulled down + together.
    verify: (score) => avg(score, ['browDownLeft', 'browDownRight']) > 0.35,
  },
  {
    key: 'smile',
    // Both mouth corners raised.
    verify: (score) => avg(score, ['mouthSmileLeft', 'mouthSmileRight']) > 0.42,
  },
  {
    key: 'brow_raise',
    // Inner or outer brows lifted (frontalis).
    verify: (score) => max(score, ['browInnerUp', 'browOuterUpLeft', 'browOuterUpRight']) > 0.4,
  },
  {
    key: 'squint',
    // Both eyes squinted (orbicularis oculi) — without a broad smile, so the
    // crow's-feet activation is isolated from the zygomaticus.
    verify: (score) =>
      avg(score, ['eyeSquintLeft', 'eyeSquintRight']) > 0.4
      && avg(score, ['mouthSmileLeft', 'mouthSmileRight']) < 0.35,
  },
  {
    key: 'lip_purse',
    // Lips pursed/puckered forward (orbicularis oris) for perioral lines.
    verify: (score) => score('mouthPucker') > 0.4,
  },

  // --- Head-angle steps (added at the end): laterals + up/down ---------------
  // These capture profile and tilted views. They skip the frontal gate and
  // verify head orientation; framing/presence/lighting still apply.
  {
    key: 'profile_left',
    kind: 'angle',
    requireFrontal: false,
    verify: (score, head = { yaw: 0 }) => head.yaw <= -LATERAL_YAW,
  },
  {
    key: 'profile_right',
    kind: 'angle',
    requireFrontal: false,
    verify: (score, head = { yaw: 0 }) => head.yaw >= LATERAL_YAW,
  },
  {
    key: 'look_up',
    kind: 'angle',
    requireFrontal: false,
    verify: (score, head = { pitch: 0 }) => head.pitch >= LOOK_PITCH,
  },
  {
    key: 'look_down',
    kind: 'angle',
    requireFrontal: false,
    verify: (score, head = { pitch: 0 }) => head.pitch <= -LOOK_PITCH,
  },
];

export const TOTAL_POSES = POSES.length;
